import React, { Fragment, Suspense, useCallback } from 'react';
import ErrorBoundary from '@helpcenter/error-boundary/src/error-boundary';
import { isHubsEnabledInHelpCenter } from '@helpcenter/feature-flags';
import { useIntl } from 'react-intl';
import Flag from '@atlaskit/flag/flag';
import FlagGroup from '@atlaskit/flag/flag-group';
import StatusError from '@atlaskit/icon/core/status-error';
import {
	AiSearchTriggerFloating,
	AiSearchTrigger,
} from '@atlassian/csm-ai-search-trigger/ai-search-trigger';
import {
	useAiWidgetConfig,
	useOptionalAiWidgetConfig,
} from '@atlassian/csm-ai-search-trigger/controllers/ai-widget-config-context';
import { AiCornerAgent } from '@atlassian/csm-ai-search-trigger/ai-corner-agent';
import { CsmBanner, type CsmBannerTextTypeProps } from '@atlassian/csm-banner/src/index';
import { useRelayResource } from '@atlassian/help-center-common-util/resource-router';

import { SupportWidgetConfigPreloadedQueryProvider } from '../services/support-widget-config-provider';
import type { supportWidgetConfigProviderQuery } from '../services/support-widget-config-provider/__generated__/supportWidgetConfigProviderQuery.graphql';
import { csmSupportWidgetConfigQueryResource } from '../services/support-widget-config-resource';
import messages from './messages';

const DEFAULT_BRANDING_COLOR = '#0074e0';

export const CSMSupportWidgetConfigProvider = ({
	children,
}: {
	children: React.ReactNode;
}): React.ReactElement | null => {
	const { queryReference } = useRelayResource<supportWidgetConfigProviderQuery>(
		csmSupportWidgetConfigQueryResource,
	);

	if (!queryReference) {
		return null;
	}

	return (
		<SupportWidgetConfigPreloadedQueryProvider queryReference={queryReference}>
			{children}
		</SupportWidgetConfigPreloadedQueryProvider>
	);
};

/**
 * Connects the AiSearchTrigger to the AiWidgetConfigProvider
 */
export const CSMSupportSiteAiSearchTrigger = () => {
	const { supportWidgetConfig } = useAiWidgetConfig();
	const { locale } = useIntl();

	return (
		<AiSearchTrigger
			locale={locale}
			site={supportWidgetConfig.site}
			siteId={supportWidgetConfig.siteId}
			widgetId={supportWidgetConfig.widgetId}
			widgetTheme={supportWidgetConfig.widgetTheme}
		/>
	);
};

/**
 * Connects the AiSearchTriggerFloating to the AiWidgetConfigProvider
 */
export const CSMSupportAgentAiSearchTriggerFloating = () => {
	const { supportWidgetConfig } = useAiWidgetConfig();
	const { locale } = useIntl();

	return (
		<AiSearchTriggerFloating
			locale={locale}
			site={supportWidgetConfig.site}
			siteId={supportWidgetConfig.siteId}
			widgetId={supportWidgetConfig.widgetId}
			widgetTheme={supportWidgetConfig.widgetTheme}
		/>
	);
};

export const CSMSupportSiteAiCornerAgent = () => {
	const { supportWidgetConfig } = useAiWidgetConfig();
	const { locale } = useIntl();

	return (
		<AiCornerAgent
			locale={locale}
			site={supportWidgetConfig.site}
			siteId={supportWidgetConfig.siteId}
			widgetId={supportWidgetConfig.widgetId}
		/>
	);
};

/**
 * Renders either the "central" floating ai search trigger or the "corner" bubble trigger
 * Depending on whether the agent is enabled and user configuration of position
 */
export const CSMSupportAgent = () => {
	const contextValue = useOptionalAiWidgetConfig();

	if (__SERVER__) {
		return null;
	}

	if (!contextValue || !contextValue.supportWidgetConfig.isEnabled) {
		return null;
	}

	if (contextValue.supportWidgetConfig.widgetPosition === 'CORNER') {
		return <CSMSupportSiteAiCornerAgent />;
	}

	return <CSMSupportAgentAiSearchTriggerFloating />;
};

const WidgetErrorBoundary = ({ children }: { children: React.ReactNode }) => {
	const { formatMessage } = useIntl();
	const renderError = useCallback(() => {
		return (
			<FlagGroup>
				<Flag
					id="csm-widget-error-flag"
					appearance="error"
					icon={<StatusError label="" />}
					title={formatMessage(messages.widgetErrorTitle)}
					description={formatMessage(messages.widgetErrorDescription)}
					testId="csm-widget-error-flag"
				/>
			</FlagGroup>
		);
	}, [formatMessage]);

	return (
		<ErrorBoundary
			packageName="csm-chat-widget"
			id="widget-config-provider"
			renderError={renderError}
		>
			{children}
		</ErrorBoundary>
	);
};

/**
 * HOC that wraps a component with the AI widget config context
 * and the AI agent UI rendering components (central floating or corner widget)
 */

export const withAiAgent = <P extends object>(
	WrappedComponent: React.ComponentType<P>,
	bannerText?: CsmBannerTextTypeProps,
	showBanner = false,
) => {
	type WithAiAgentProps = P & { bannerBackgroundColor?: string };
	const WithAiAgent = (props: WithAiAgentProps) => {
		const { bannerBackgroundColor = DEFAULT_BRANDING_COLOR, ...componentProps } = props;
		return (
			<Fragment>
				{isHubsEnabledInHelpCenter() && showBanner && (
					<Suspense fallback={null}>
						<CSMSupportWidgetConfigProvider>
							<CsmBanner bannerBackgroundColor={bannerBackgroundColor} bannerText={bannerText} />
						</CSMSupportWidgetConfigProvider>
					</Suspense>
				)}
				<WrappedComponent {...(componentProps as P)} />
				<WidgetErrorBoundary>
					<Suspense fallback={null}>
						<CSMSupportWidgetConfigProvider>
							<CSMSupportAgent />
						</CSMSupportWidgetConfigProvider>
					</Suspense>
				</WidgetErrorBoundary>
			</Fragment>
		);
	};

	WithAiAgent.displayName = `WithAiAgent(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

	return WithAiAgent;
};
